/**
 * Employee self-service: My Leave screen (Phase 5b).
 *
 * Shows balance cards (Total / Taken / Pending / Available per leave type,
 * the same figures as the admin Balances tab), request history, and a "Request leave" form backed
 * by the ESS procedures added in Phase 4 (#wagepro-leave-phase4):
 *   - personalPayroll.listMyLeaveBalances  → balance cards + type picker
 *   - personalPayroll.listMyLeaveRequests  → history list
 *   - personalPayroll.submitLeaveRequest   → "Request leave" form
 *   - personalPayroll.cancelMyLeaveRequest → cancel pending request
 *
 * State machine mirrors MyPayScreen exactly; step-up uses the same shared
 * mechanics (restartSignIn / resolveLedgerWebUrl) but with my-leave-specific
 * copy so the screen is self-contained.
 */
import { useId, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  GlassCard,
  StatusPill,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header.js';
import { useIsEssSurface } from '../../components/layout/ess_surface.js';
import { useMyLeave } from '../../data/use_my_leave.js';
import { MyLeaveStepUpPanel } from './my_leave_step_up_panel.js';
import { LeaveRequestForm } from './my_leave_request_form.js';
import { MyLeaveBalanceSummary } from './my_leave_balance_summary.js';
import {
  format_days_scaled,
  normalise_leave_status,
  sort_requests_desc,
  type MyLeaveRequest,
} from './my_leave_utils.js';
import { LEAVE_STATUS_PILL } from '../../lib/label_keys.js';
import { format_date, format_date_range } from '../../lib/format.js';

export function MyLeaveScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, retry, submit, cancel } = useMyLeave();
  const [show_form, set_show_form] = useState(false);
  const [cancel_target, set_cancel_target] = useState<MyLeaveRequest | null>(
    null,
  );
  const [cancel_in_progress, set_cancel_in_progress] = useState(false);
  const [cancel_error, set_cancel_error] = useState<string | null>(null);
  // Phone layout on the employee surface only; the employer console's own
  // My leave route keeps the original layout (components/layout/ess_surface.tsx).
  const ess = useIsEssSurface();
  const history_heading_id = useId();

  // ── Loading ───────────────────────────────────────────────────────────────
  if (state.kind === 'loading') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_leave.title')} />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <span className="sr-only">{t('my_leave.loading')}</span>
          <div className="nv-skel" style={{ height: 80, margin: 8 }} />
          <div className="nv-skel" style={{ height: 80, margin: 8 }} />
        </GlassCard>
      </div>
    );
  }

  // ── AAL2 step-up required ─────────────────────────────────────────────────
  if (state.kind === 'step_up_required') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_leave.title')} />
        <MyLeaveStepUpPanel />
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (state.kind === 'error') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_leave.title')} />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <EmptyState
            title={t('my_leave.error')}
            action={
              <Button variant="secondary" onClick={retry}>
                {t('my_leave.retry')}
              </Button>
            }
          />
        </GlassCard>
      </div>
    );
  }

  // ── No employer link ──────────────────────────────────────────────────────
  if (state.kind === 'no_employer_link') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_leave.title')} />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <EmptyState title={t('my_leave.no_employer_link')} />
        </GlassCard>
      </div>
    );
  }

  // ── Ready ─────────────────────────────────────────────────────────────────
  const { balances, leave_types, requests } = state;
  const sorted_requests = sort_requests_desc(requests);

  const handle_confirm_cancel = (): void => {
    if (!cancel_target) return;
    const req_id = cancel_target.id;
    set_cancel_in_progress(true);
    set_cancel_error(null);
    void cancel(req_id)
      .then((result) => {
        if (result.outcome === 'success') {
          set_cancel_target(null);
        } else {
          set_cancel_error(
            result.message ?? t('my_leave.cancel_error_generic'),
          );
        }
      })
      .finally(() => {
        set_cancel_in_progress(false);
      });
  };

  return (
    <div className="max-w-[720px] mx-auto">
      <ScreenHeader
        title={t('my_leave.title')}
        description={t('my_leave.subtitle', {
          employer: state.employer_name,
        })}
      />

      {/* ── Balance cards ─────────────────────────────────────────────── */}
      {balances.length > 0 && (
        <section aria-label={t('my_leave.balances_section_label')}>
          <div
            className={
              balances.length > 1
                ? 'grid grid-cols-1 gap-3 mb-4 sm:grid-cols-2'
                : 'grid grid-cols-1 gap-3 mb-4'
            }
          >
            {balances.map((bal) => (
              <MyLeaveBalanceSummary key={bal.leave_type} balance={bal} />
            ))}
          </div>
        </section>
      )}

      {/* ── Request leave form ──────────────────────────────────────────── */}
      {leave_types.length > 0 && (
        <GlassCard
          className={
            ess ? 'wp-ess-request-card mb-4 p-5' : 'rounded-2xl p-5 mb-4'
          }
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
            <h2 className="font-bold text-[15px]">
              {t('my_leave.request_section_title')}
            </h2>
            {!show_form && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  set_show_form(true);
                }}
              >
                {t('my_leave.request_cta')}
              </Button>
            )}
            {show_form && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  set_show_form(false);
                }}
              >
                {t('my_leave.cancel_form_cta')}
              </Button>
            )}
          </div>

          {show_form && (
            <LeaveRequestForm
              leave_types={leave_types}
              balances={balances}
              submit={submit}
              on_success={() => {
                set_show_form(false);
              }}
            />
          )}
        </GlassCard>
      )}

      {/* ── Request history ─────────────────────────────────────────────── */}
      <div className="mt-2">
        <h2
          id={history_heading_id}
          className={
            ess
              ? 'mb-2 text-[17px] font-bold tracking-[-0.01em]'
              : 'font-bold mb-2 text-[14px]'
          }
        >
          {t('my_leave.history_title')}
        </h2>
        {sorted_requests.length === 0 ? (
          <GlassCard className="rounded-2xl p-5">
            <EmptyState title={t('my_leave.history_empty')} />
          </GlassCard>
        ) : ess ? (
          /*
           * Phone layout: one card per request instead of the five-column
           * table below (a phone has no room for it). Same rows, order and
           * Cancel action. Each card is a <dl> whose term/description pairs
           * keep the table's column names for assistive tech as
           * visually-hidden terms (the design shows the values alone). The
           * second line reads "22–23 Oct 2026 · 2 days" and never wraps
           * (WagePro bug-bash 2026-10-15, #11): the range collapses the
           * shared month and year, and the status pill sits beside the type
           * line so it no longer squeezes the dates.
           */
          <ul
            aria-labelledby={history_heading_id}
            className="flex flex-col gap-2.5"
          >
            {sorted_requests.map((req) => {
              const status = normalise_leave_status(req.status);
              const pill_status = LEAVE_STATUS_PILL[status];
              const days = format_days_scaled(req.days_requested);
              const can_cancel = status === 'pending';
              return (
                <li
                  key={req.id}
                  className="glass flex flex-col gap-1.5 rounded-[20px] px-4 py-3.5"
                  data-testid="my-leave-request-row"
                >
                  <dl className="wp-ess-request-dl">
                    <div className="wp-ess-request-type">
                      <dt className="sr-only">{t('my_leave.col_type')}</dt>
                      <dd className="text-[15px] font-bold">
                        {t(`my_leave.leave_type.${req.leave_type}`, {
                          defaultValue: req.leave_type,
                        })}
                      </dd>
                    </div>
                    <div className="wp-ess-request-dates">
                      <dt className="sr-only">{t('my_leave.col_dates')}</dt>
                      <dd>
                        {format_date_range(
                          req.start_date,
                          req.end_date,
                          locale,
                        )}
                      </dd>
                    </div>
                    <div className="wp-ess-request-days">
                      {/* The value already says "2 days"; a spoken
                          "Days" term would only repeat it. */}
                      <dt className="sr-only" aria-hidden="true">
                        {t('my_leave.col_days')}
                      </dt>
                      <dd className="num font-semibold text-[color:var(--ink)]">
                        {t('my_leave.days_count', { count: days })}
                      </dd>
                    </div>
                    <div className="wp-ess-request-status">
                      <dt className="sr-only">{t('my_leave.col_status')}</dt>
                      <dd>
                        <StatusPill status={pill_status} />
                      </dd>
                    </div>
                  </dl>
                  {can_cancel && (
                    <div className="flex justify-end">
                      <Button
                        variant="ghost-danger"
                        size="sm"
                        onClick={() => {
                          set_cancel_target(req);
                          set_cancel_error(null);
                        }}
                      >
                        {t('my_leave.cancel_request_cta')}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <GlassCard className="rounded-2xl p-5">
            <div className="overflow-x-auto">
              <table className="nv-table w-full">
                <thead>
                  <tr>
                    <th>{t('my_leave.col_type')}</th>
                    <th>{t('my_leave.col_dates')}</th>
                    <th className="text-right">{t('my_leave.col_days')}</th>
                    <th>{t('my_leave.col_status')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sorted_requests.map((req) => {
                    const status = normalise_leave_status(req.status);
                    const pill_status = LEAVE_STATUS_PILL[status];
                    const days = format_days_scaled(req.days_requested);
                    const can_cancel = status === 'pending';
                    return (
                      <tr key={req.id}>
                        <td>
                          {t(`my_leave.leave_type.${req.leave_type}`, {
                            defaultValue: req.leave_type,
                          })}
                        </td>
                        <td className="whitespace-nowrap">
                          {format_date(req.start_date, locale)}
                          {req.start_date !== req.end_date && (
                            <> – {format_date(req.end_date, locale)}</>
                          )}
                        </td>
                        <td className="text-right num">{days}</td>
                        <td>
                          <StatusPill status={pill_status} />
                        </td>
                        <td className="text-right">
                          {can_cancel && (
                            <Button
                              variant="ghost-danger"
                              size="sm"
                              onClick={() => {
                                set_cancel_target(req);
                                set_cancel_error(null);
                              }}
                            >
                              {t('my_leave.cancel_request_cta')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </div>

      {/* ── Cancel confirm dialog ─────────────────────────────────────── */}
      <ConfirmDialog
        open={cancel_target !== null}
        onOpenChange={(open) => {
          if (!open) {
            set_cancel_target(null);
            set_cancel_error(null);
          }
        }}
        title={t('my_leave.cancel_dialog_title')}
        description={t('my_leave.cancel_dialog_description')}
        confirmLabel={t('my_leave.cancel_dialog_confirm_label')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={cancel_in_progress}
        onConfirm={handle_confirm_cancel}
      >
        {cancel_error && (
          <p className="nv-help is-error" role="alert">
            {cancel_error}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
